import { useState, useEffect, useRef } from "react"

type Result = { id: number; name: string }

export function EntityCombobox({
  type,
  initialId,
  initialName,
  placeholder = "Search…",
  onSelect,
  inputClass,
}: {
  type: string
  initialId: string
  initialName: string
  placeholder?: string
  onSelect: (id: string, name: string) => void
  inputClass: string
}) {
  const [text, setText]           = useState(initialName)
  const [selectedId, setSelectedId] = useState(initialId)
  const [results, setResults]     = useState<Result[]>([])
  const [open, setOpen]           = useState(false)
  const containerRef              = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setSelectedId(initialId)
    setText(initialName)
  }, [initialId, initialName])

  useEffect(() => {
    if (selectedId || text.length < 2) { setResults([]); setOpen(false); return }
    const timer = setTimeout(async () => {
      const res  = await fetch(`/admin/entities/search?type=${encodeURIComponent(type)}&q=${encodeURIComponent(text)}`)
      const data: Result[] = await res.json()
      setResults(data)
      setOpen(data.length > 0)
    }, 250)
    return () => clearTimeout(timer)
  }, [text, selectedId, type])

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", handleClick)
    return () => document.removeEventListener("mousedown", handleClick)
  }, [])

  function clear() {
    setSelectedId("")
    setText("")
    onSelect("", "")
  }

  function pick(result: Result) {
    setSelectedId(String(result.id))
    setText(result.name)
    setResults([])
    setOpen(false)
    onSelect(String(result.id), result.name)
  }

  return (
    <div ref={containerRef} className="relative">
      <div className={`${inputClass} flex items-center gap-2 !p-0 overflow-hidden`}>
        <input
          type="text"
          value={text}
          onChange={(e) => { clear(); setText(e.target.value) }}
          placeholder={placeholder}
          className="flex-1 px-3 py-2 text-sm bg-transparent outline-none"
        />
        {selectedId && (
          <button
            type="button"
            onClick={clear}
            className="px-2 py-2 text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 text-lg leading-none"
            aria-label="Clear"
          >
            ×
          </button>
        )}
      </div>
      {open && (
        <ul className="absolute z-20 top-full left-0 right-0 mt-1 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-lg shadow-lg overflow-hidden max-h-48 overflow-y-auto">
          {results.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(r)}
                className="w-full text-left px-3 py-2 text-sm text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors"
              >
                {r.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
